// React
import { memo, useState, useEffect } from "react";
import PropTypes from "prop-types";
// Components
import MultipleTextFieldGroup from "@/Layouts/components/MultipleTextFieldGroup/MultipleTextFieldGroup";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
// MUI
import { Button, Grid, Typography, Dialog, DialogContent, DialogTitle, IconButton, CircularProgress, Tooltip } from "@mui/material";
// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import yellowAlert from "@/assets/icons/settings/yellow_alert.svg";
import mainAccountIcon from "@/assets/icons/settings/mainFsAccount.svg";

FinancialStatementConfigPage.propTypes = {
	updateGLAccounts: PropTypes.func.isRequired,
	page: PropTypes.object.isRequired,
	versions: PropTypes.array.isRequired,
	isLoading: PropTypes.bool.isRequired,
	accountTypes: PropTypes.array.isRequired,
};
function RenderSubRow(props) {
	const {
		parentRow,
		subAccountRow,
		onHandleAdd,
		onHandleActionUpdate,
		onHandleValidation,
		onHandleDelete,
		onTypeChange,
		validateSimpleValue,
		isLoading,
		localLoading,
		accountTypes,
	} = props;

	const items = subAccountRow?.glAccounts?.length >= 1 ? subAccountRow.glAccounts : [""];

	return (
		<Grid mb={"5px"} container spacing={0} sx={{ marginLeft: "25px", alignItems: "top" }}>
			<Grid item xs={1} sx={{ minWidth: "300px", display: "flex", alignItems: "center", height: "100%" }}>
				<Typography className="financial-statement-config-content"> </Typography>
			</Grid>
			<Grid item sx={{ width: "14px", alignItems: "right", marginRight: "4px" }}>
				{/* <img src={mainAccountIcon} style={{width:"13.5px"}}/> */}
			</Grid>
			<Grid item xs={1} sx={{ marginRight: "50px" }}>
				{subAccountRow.warning ? (
					<Tooltip
						arrow
						placement="bottom-start"
						title={
							<Typography sx={{ fontFamily: "PTSansProNarrow", color: "white", fontSize: "14px" }}>
								{"Account is potentially unmapped: " + (subAccountRow.warning || "")}
							</Typography>
						}
					>
						<Typography
							sx={{ display: "flex", alignItems: "center" }}
							className={
								subAccountRow.warning
									? "financial-statement-config-content-error"
									: "financial-statement-config-sub-content"
							}
						>
							{subAccountRow.subAccount}{" "}
							{subAccountRow.warning ? <img src={yellowAlert} style={{ marginLeft: "2px", width: "12px" }} /> : ""}
						</Typography>
					</Tooltip>
				) : (
					<Typography className={"financial-statement-config-sub-content"}>{subAccountRow.subAccount}</Typography>
				)}
			</Grid>
			{subAccountRow.error && !subAccountRow.error.includes("Page") ? (
				<Grid item ml={"-20px"} mt={"-2px"}>
					<Tooltip
						arrow
						placement="bottom-start"
						title={
							<Typography sx={{ fontFamily: "PTSansProNarrow", color: "white", fontSize: "14px" }}>
								{"Potentially unrecognized accounts: " + (subAccountRow.error || "")}
							</Typography>
						}
					>
						<img
							src={yellowAlert}
							className={"financial-statement-config-content-error"}
							style={{ marginRight: "6px", width: "14px" }}
						/>
					</Tooltip>
				</Grid>
			) : (
				""
			)}
			<Grid item mt={"-10px"} xs={7} sx={{ minWidth: "100px" }}>
				<MultipleTextFieldGroup
					sx={{ fontFamily: "PTSansProNarrow", fontSize: "20px", minWidth: "50px" }}
					textFieldStyle={{ minWidth: "75px", width: "75px" }}
					onAdd={(value) => onHandleAdd(value, subAccountRow._id, parentRow)}
					from={subAccountRow._id}
					placeholder="Account #"
					items={items.map((glItem) => {
						return {
							typeError: subAccountRow.typeError,
							glAccount: glItem.glAccount,
							value: glItem.glAccount,
							accountType: glItem.accountType,
						};
					})}
					onActionUpdate={(value, from, idx) => onHandleActionUpdate(value, from, idx, parentRow)}
					onDelete={(idx, from) => onHandleDelete(idx, from, parentRow)}
					simpleValidation={(value) => validateSimpleValue(value, value.error)}
					validation={(value) => onHandleValidation(value.value)}
					hideTextField={subAccountRow.hideInput}
					openTooltip={items.length > 0}
					warningList={subAccountRow.error && !subAccountRow.error.includes("Page") ? subAccountRow.error.split(",") : []}
					tooltipMessage={subAccountRow.error}
					showDeleteInFirst={items.length === 1 && !subAccountRow.hideInput}
					deleting={isLoading || localLoading}
					isLoading={isLoading || localLoading}
					readOnly={isLoading || localLoading}
					showAdvancedSelect={true}
					accountTypes={accountTypes}
					onTypeChange={(value, from, idx) => onTypeChange(value, from, idx, parentRow)}
				/>
			</Grid>
		</Grid>
	);
}

const RenderSubRowMemo = memo(RenderSubRow);

const headerRow = (key, label) => {
	return (
		<Grid container mb={"32px"} key={key} spacing={3}>
			<Grid item xs={12}>
				<Typography className="financial-statement-config-sub-title">{label}</Typography>
			</Grid>
		</Grid>
	);
};

const footerRow = (key, label) => {
	return (
		<Grid container mb={"32px"} key={key} spacing={3}>
			<Grid item xs={12} sx={{ marginLeft: "25px" }}>
				<Typography className="financial-statement-config-sub-title">{label}</Typography>
			</Grid>
		</Grid>
	);
};
const renderMainRow = (
	row,
	onHandleAdd,
	onHandleActionUpdate,
	onHandleValidation,
	onHandleDelete,
	onTypeChange,
	validateSimpleValue,
	isLoading,
	localLoading,
	accountTypes
) => {
	switch (row.contentType) {
		case "COLUMN":
		case "COLUMN_GROUP":
			return headerRow(row._id, row.label);
		case "FOOTER":
		case "SUB_FOOTER":
			return footerRow(row._id, row.label);
		case "ROW": {
			const disableRow = !/^[0-9]/.test(row.account);
			const glItems = row.glAccounts?.length >= 1 ? row.glAccounts : [""];
			return (
				<Grid key={row._id} mb={"5px"} container spacing={0} sx={{ marginLeft: "25px", alignItems: "top" }}>
					<Grid item xs={1} sx={{ minWidth: "300px", display: "flex", alignItems: "center", height: "100%" }}>
						<Typography
							className="financial-statement-config-content"
							style={{
								maxWidth: "270px", // Adjust the value as needed
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{row.label}
						</Typography>
					</Grid>
					<Grid item mt={"5px"} sx={{ width: "14px", alignItems: "center", marginRight: "4px" }}>
						{row?.account?.join() ? <img src={mainAccountIcon} style={{ width: "13.5px", alignItems: "center" }} /> : ""}
					</Grid>
					<Grid item xs={1} sx={{ marginRight: "50px" }}>
						{row.warning ? (
							<Tooltip
								arrow
								placement="bottom-start"
								title={
									<Typography sx={{ fontFamily: "PTSansProNarrow", color: "white", fontSize: "14px" }}>
										{"Account is potentially unmapped: " + (row.warning || "")}
									</Typography>
								}
							>
								<Typography
									sx={{ display: "flex", alignItems: "center" }}
									className={
										row.warning ? "financial-statement-config-content-error" : "financial-statement-config-content"
									}
								>
									{row.account?.join()}{" "}
									{row.warning ? <img src={yellowAlert} style={{ marginLeft: "2px", width: "12px" }} /> : ""}
								</Typography>
							</Tooltip>
						) : (
							<Typography className={"financial-statement-config-main-account"}>{row?.account?.join()}</Typography>
						)}
					</Grid>

					{row.error && !row.error.includes("Page") ? (
						<Grid item ml={"-20px"} mt={"5px"}>
							<Tooltip
								arrow
								placement="bottom-start"
								title={
									<Typography sx={{ fontFamily: "PTSansProNarrow", color: "white", fontSize: "14px" }}>
										{"Potentially unrecognized accounts: " + (row.error || "")}
									</Typography>
								}
							>
								<img
									src={yellowAlert}
									className={"financial-statement-config-content-error"}
									style={{ marginRight: "6px", width: "14px" }}
								/>
							</Tooltip>
						</Grid>
					) : (
						""
					)}

					<Grid item mt={"-10px"} xs={7} sx={{ minWidth: "100px" }}>
						<MultipleTextFieldGroup
							sx={{ width: "100px", fontFamily: "PTSansProNarrow", fontSize: "20px" }}
							textFieldStyle={{ minWidth: "75px", width: "75px" }}
							onAdd={(value) => onHandleAdd(value, row._id)}
							from={row._id}
							placeholder="Account #"
							items={glItems.map((glItem) => {
								return {
									...glItem,
									typeError: row.typeError,
									glAccount: glItem.glAccount,
									value: glItem.glAccount,
									accountType: glItem.accountType,
								};
							})}
							onActionUpdate={(value, from, idx) => onHandleActionUpdate(value, from, idx)}
							onDelete={onHandleDelete}
							simpleValidation={(value) => validateSimpleValue(value, value.error)}
							validation={(value) => onHandleValidation(value.value)}
							openTooltip={glItems.length > 0}
							tooltipMessage={row.error}
							warningList={row.error && !row.error.includes("Page") ? row.error.split(",") : []}
							hideTextField={!row.isInRelations || disableRow || row.hideInput}
							// hideTextField={!row.isInRelations}
							showDeleteInFirst={row.isInRelations && !disableRow && !row.hideInput}
							deleting={!row.isInRelations || localLoading}
							readOnly={isLoading || localLoading}
							isLoading={!row.isInRelations || disableRow || localLoading}
							showAdvancedSelect={true}
							accountTypes={accountTypes}
							onTypeChange={(value, from, idx) => onTypeChange(value, from, idx)}
						/>
					</Grid>
				</Grid>
			);
		}
		default:
			break;
	}
};

export default function FinancialStatementConfigPage(props) {
	const [openCopy, setOpenCopy] = useState(false);
	const [page, setPage] = useState(props.page || []);
	const [versionToCopy, setVersionToCopy] = useState(props.versions[0]);
	const [isLoading, setIsLoading] = useState(false);
	const [localLoading, setLocalLoading] = useState(false);
	// HANDLE ACTIONS
	const onTypeChange = (value, from, idx, parentAccount) => {
		setLocalLoading(true);
		const newPage = { ...page };
		let relations;
		if (parentAccount) {
			let newRow = newPage.content.find((row) => row._id === parentAccount._id);
			newRow = { ...newRow, glAccounts: newRow.relations.find((subRow) => subRow._id === from).glAccounts };
			newRow.glAccounts[idx] = { ...newRow.glAccounts[idx], accountType: value };
			relations = {
				subAccount: newRow.relations.find((subRow) => subRow._id === from).subAccount,
				glAccountValue: newRow.glAccounts[idx].glAccount,
				GLIndex: idx,
				hideInput: newRow.relations.find((subRow) => subRow._id === from).hideInput,
				accountType: value.accountType,
			};
			props.updateGLAccounts("UPDATE", value, from, newRow.account, newRow.label, relations);
		} else {
			let newRow = newPage.content.find((row) => row._id === from);
			newRow = { ...newRow, glAccounts: newRow.relations.find((subRow) => subRow.subAccount === newRow.account.join()).glAccounts };
			newRow.glAccounts[idx] = { ...newRow.glAccounts[idx], accountType: value };
			relations = {
				subAccount: newRow.relations.find((subRow) => subRow.subAccount === newRow.account.join()).subAccount,
				glAccountValue: newRow.glAccounts[idx].glAccount,
				GLIndex: idx,
				hideInput: newRow.relations.find((subRow) => subRow.subAccount === newRow.account.join()).hideInput,
				accountType: value.accountType,
			};
			props.updateGLAccounts("UPDATE", value, from, newRow.account, newRow.label, relations);
		}
		setPage(newPage);
	};

	const onHandleActionUpdate = (value, from, idx, parentAccount) => {
		//This state is set to false when the loading of the mutation finish.
		setLocalLoading(true);
		value = value.toUpperCase();
		let newPage = { ...page };
		let newRow;
		let relations;
		let subAccountRow;
		//if parentAccount is sent, is because the update input is a subAccount
		if (parentAccount) {
			newRow = newPage.content.find((row) => row._id === parentAccount._id);
			subAccountRow = newRow?.relations?.find((subRow) => subRow._id === from);
			relations = {
				subAccount: subAccountRow.subAccount,
				glAccountValue: value,
				GLIndex: idx,
				hideInput: subAccountRow.hideInput,
				accountType: subAccountRow.glAccounts[idx]?.accountType,
			};
		} else {
			//if not sent the parent row, is because is the main account to be updated
			newRow = newPage.content.find((row) => row._id === from);
			subAccountRow = newRow?.relations?.find((subRow) => subRow?.subAccount === newRow?.account?.join());
			relations = {
				subAccount: subAccountRow.subAccount,
				glAccountValue: value,
				GLIndex: idx,
				hideInput: newRow.hideInput,
				accountType: subAccountRow.glAccounts[idx]?.accountType,
			};
		}
		if (newRow) {
			if (parentAccount) {
				newRow = { ...newRow, glAccounts: subAccountRow.glAccounts };
			}
			const foundIn = props.updateGLAccounts("UPDATE", value, from, newRow.account, newRow.label, relations);
			if (foundIn) {
				subAccountRow.error = `In Page ${foundIn.pageNumber}, account ${foundIn.account}`;
				if (subAccountRow.glAccounts?.length) {
					subAccountRow.glAccounts[idx].error = true;
					subAccountRow.glAccounts[idx].value = value;
					subAccountRow.glAccounts[idx].glAccount = value;
				} else {
					subAccountRow.glAccounts = [{ error: true, value }];
				}
				// As was not updated in the db, we can stop the local loading
				setLocalLoading(false);
			} else if (subAccountRow.glAccounts?.length) {
				subAccountRow.glAccounts[idx] = { glAccount: value };
			} else {
				subAccountRow.glAccounts = [{ glAccount: value }];
			}
		}
		setLocalLoading(false);
		setPage(newPage);
	};

	const onHandleValidation = (values) => {
		// IF THERE IS A GL ACCOUNT WITH ERROR:
		if (values?.filter((value) => value.error === true || !value.length).length > 0) return false;
		// IF THERE IS NOT VALUES OR LENGTH 0,
		return !(!values || values.length === 0 || !values[values?.length - 1]?.length);
	};

	const onHandleAdd = (values, _id, parentRow) => {
		const newPage = { ...page };
		let newRow;
		//If parentRow is sent, the modified row was a subAccount
		if (parentRow) {
			newRow = newPage.content.find((row) => row._id === parentRow._id)?.relations?.find((subrow) => subrow._id === _id);
		} else {
			newRow = newPage.content.find((row) => row._id === _id);
			newRow = newRow?.relations?.find((subRow) => subRow?.subAccount === newRow?.account?.join());
		}
		if (newRow.hideInput) {
			newRow.hideInput = false;
			setPage(newPage);
			return true;
		}
		if (!values[values?.length - 1]?.glAccount?.length) {
			return false;
		}
		newRow?.glAccounts?.push({ glAccount: "", accountType: "" });
		setPage(newPage);
	};

	const onHandleDelete = (idx, from, parentRow) => {
		//This state is set to false when the loading of the mutation finish.
		setLocalLoading(true);
		const newPage = { ...page };
		let row;
		let subAccountRow;

		if (parentRow) {
			row = newPage?.content?.find((rowInPage) => rowInPage._id === parentRow._id);
			subAccountRow = row?.relations?.find((rowInPage) => rowInPage._id === from);
		} else {
			row = newPage?.content?.find((rowInPage) => rowInPage._id === from);
			subAccountRow = row?.relations?.find((subRow) => subRow?.subAccount === row?.account?.join());
		}
		//remove the glAccount of the event
		const newGeneralLedgerAcc = subAccountRow?.glAccounts?.filter((_, index) => index !== idx);
		subAccountRow.glAccounts = newGeneralLedgerAcc;
		//if first glAccount was deleted, set hideInput true
		if (idx === 0 && (!subAccountRow?.glAccounts || subAccountRow.glAccounts.length === 0)) {
			subAccountRow.hideInput = true;
		}
		//object relation to update in the db
		const relations = {
			subAccount: subAccountRow.subAccount,
			glAccountValue: "",
			GLIndex: idx,
			hideInput: subAccountRow.hideInput,
		};
		props.updateGLAccounts("DELETE", "", from, row.account, row.label, relations);
		setPage(newPage);
	};

	const validateSimpleValue = (glAccount, error) => {
		return error || glAccount?.value?.length > 0;
	};

	const getCopyAccDialog = (versions) => {
		// if (versions) return;
		return (
			<Dialog
				className={"dialog-br-15"}
				open={openCopy}
				onClose={() => setOpenCopy(false)}
				PaperProps={{
					style: {
						overflow: "hidden",
					},
				}}
			>
				<DialogTitle>
					<Typography className={"regular-montserrat-label fs-24"} sx={{ lineHeight: "1.2" }}></Typography>
					<IconButton
						aria-label="close"
						onClick={() => setOpenCopy(false)}
						sx={{
							position: "absolute",
							right: 5,
							top: 2,
							color: (theme) => theme.palette.grey[500],
						}}
					>
						<CloseRoundedIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent style={{ overflow: "hidden", marginBottom: "30px" }}>
					<Grid container direction="column" justifyContent="center" alignItems="center">
						<Typography className="financial-statement-config-content" sx={{ margin: "20px 20px 20px 20px" }}>
							Select version to copy General Ledger account numbers:
						</Typography>
						<Grid item xs={2} mb={"30px"} sx={{ minWidth: "210px" }}>
							<SmartSelector
								showBy={"version"}
								selectBy={"version"}
								options={versions ?? []}
								selected={versionToCopy}
								inputProps={{
									placeholder: versions?.length > 0 ? "Select Version" : "No version available",
								}}
								clearable
								listBoxProps={{
									sx: { maxHeight: "120px" },
								}}
								popperProps={{ className: "!z-[1350]" }}
								onChangeAction={(selected) => {
									setVersionToCopy(selected);
								}}
							/>
						</Grid>
						<Button
							variant="contained"
							className={"confirm-copy-btn"}
							disabled={isLoading || !versionToCopy}
							onClick={() => props.copyGLAccounts(versionToCopy)}
						>
							{isLoading ? <CircularProgress size={24} /> : "Confirm"}
						</Button>
					</Grid>
				</DialogContent>
			</Dialog>
		);
	};

	useEffect(() => {
		const newPage = { ...props.page };
		setPage(newPage);
	}, [props.page]);

	useEffect(() => {
		setIsLoading(props.isLoading);
		if (!props.isLoading) {
			setLocalLoading(false);
			setOpenCopy(false);
		}
	}, [props.isLoading]);

	useEffect(() => {
		setVersionToCopy(null);
	}, [props.versions]);

	const renderRows = (row) => {
		try {
			const subAccountsToRender = [];
			const relationRow = row?.relations?.find((relation) => relation?.subAccount === row.account?.join());
			if (relationRow) {
				row.isInRelations = true;
				row.glAccounts = relationRow.glAccounts;
				row.hideInput = relationRow.hideInput;
				row.error = relationRow.error;
				row.warning = relationRow.warning;
				row.typeError = relationRow.typeError;
			}
			try {
				subAccountsToRender.push(
					renderMainRow(
						row,
						onHandleAdd,
						onHandleActionUpdate,
						onHandleValidation,
						onHandleDelete,
						onTypeChange,
						validateSimpleValue,
						isLoading,
						localLoading,
						props.accountTypes
					)
				);
			} catch (e) {
				console.log("cached error", row);
			}

			if (row?.relations?.length > 0) {
				row?.relations.map((relation) => {
					if (!row.account.includes(relation.subAccount)) {
						subAccountsToRender.push(
							<RenderSubRowMemo
								key={relation._id}
								parentRow={row}
								subAccountRow={relation}
								onHandleAdd={onHandleAdd}
								onHandleActionUpdate={onHandleActionUpdate}
								onHandleValidation={onHandleValidation}
								onHandleDelete={onHandleDelete}
								onTypeChange={onTypeChange}
								validateSimpleValue={validateSimpleValue}
								isLoading={isLoading}
								localLoading={localLoading}
								accountTypes={props.accountTypes}
							/>
						);
					}
				});
			}
			return subAccountsToRender;
		} catch (e) {
			console.log("Error rendering rows", e);
		}
	};

	return (
		<>
			<Grid container mt={"2px"} mb={"10px"} spacing={3}>
				<Grid item xs={12}>
					<Typography className="page-title">{page.pageName}</Typography>
				</Grid>
				<Grid item sx={{ minWidth: "300px" }}>
					<Typography className="regular-montserrat-label fs-18">Model</Typography>
				</Grid>
				<Grid item sx={{ alignItems: "right" }}>
					{/* <img src={mainAccountIcon} style={{width:"13.5px"}}/> */}
				</Grid>
				<Grid item sx={{ minWidth: "160px" }}>
					<Typography className="regular-montserrat-label fs-18">FS Acc. #</Typography>
				</Grid>
				<Grid item sx={{ minWidth: "175px" }}>
					<Grid container justifyContent="space-between" alignItems="center">
						<Typography className="regular-montserrat-label fs-18">GL Acc. #(s)</Typography>
						<Button onClick={() => setOpenCopy(true)}>
							<ContentCopyIcon style={{ color: "var(--text-color)" }} />
						</Button>
					</Grid>
				</Grid>
			</Grid>
			{page.content.map((row) => {
				return renderRows(row);
			})}
			{/* COPY ACCOUNTS DIALOG */}
			{getCopyAccDialog(props.versions)}
		</>
	);
}
